// @story HIR-HR5
// "Act now": offers that were declined or lapsed, each with the next-ranked
// candidates for that role. A no does not end the search — but the next offer
// is HR's decision, so nothing here offers anyone. "Open" goes to the role's
// offer screen; "Done" closes the item (recorded with who).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { offerLetterApi } from '../../services/offerLetter.service';

const REASON = { declined: 'declined', lapsed: 'lapsed — no answer by the deadline' };

export default function OfferFollowUps({ onChanged }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);

  const load = () => offerLetterApi.getFollowUps()
    .then((r) => { setItems(r.data || []); setErr(''); })
    .catch(() => setErr('Could not load the list.'));
  useEffect(() => { load(); }, []);

  const done = async (resumeId) => {
    setBusy(resumeId);
    try { await offerLetterApi.closeFollowUp(resumeId, ''); await load(); onChanged && onChanged(); }
    catch (e) { setErr(e?.message || 'Could not update the list.'); }
    finally { setBusy(null); }
  };

  if (err) return <div className="text-sm text-red-600 py-2">{err}</div>;
  if (!items) return <div className="text-sm text-gray-500 py-2 flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  if (!items.length) return <div className="text-sm text-gray-500 py-2">Nothing waiting.</div>;

  return (
    <div className="mt-2 mb-3 space-y-2">
      {items.map((it) => (
        <div key={it.resumeId} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-gray-800">
              <span className="font-semibold">{it.role}</span>: {it.candidateName}'s offer {REASON[it.reason] || it.reason}
              {it.at && <span className="text-xs text-gray-500"> · {new Date(it.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })}</span>}
            </div>
            <div className="flex gap-1.5">
              {it.jobId && (
                <button onClick={() => navigate(`/jobs/${it.jobId}`)} className="text-xs px-2 py-1 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">Open role</button>
              )}
              <button onClick={() => done(it.resumeId)} disabled={busy === it.resumeId}
                title="You have made the next offer, or decided not to. Removes this from the list; who and when are recorded."
                className="text-xs px-2 py-1 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {busy === it.resumeId ? 'Saving…' : 'Done'}
              </button>
            </div>
          </div>
          <div className="mt-1.5 text-xs text-gray-600">
            {it.next && it.next.length ? (
              <>
                <span className="text-gray-500">Next for this role: </span>
                {it.next.map((n, i) => (
                  <span key={n.resumeId}>
                    {i > 0 && <span className="text-gray-400"> · </span>}
                    <span className="font-medium text-gray-800">{i + 1}. {n.candidateName}</span>
                    <span className="text-gray-500"> ({n.why}{n.offerReady ? ', offer drafted' : ''})</span>
                  </span>
                ))}
              </>
            ) : <span className="text-amber-800">No one else is in line for this role — it may need advertising again.</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
